import { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, ApiError } from "@/lib/api";
import { FolderPicker } from "@/components/folder-picker";
import type { Project, ProjectUsage } from "@/lib/types";

/**
 * The two project forms the Projects page (`projects-view.tsx`) opens: register or
 * edit one, and delete one.
 *
 * Worth being blunt in the copy rather than only in the code: a card that resolves
 * to a project is a card an unattended agent run will open, edit and run commands
 * inside. So registering is the consent step — the path is typed once,
 * deliberately, and never inferred from something someone wrote in a comment.
 *
 * There is no archive, unlike boards. Archiving a project hid it from the pickers
 * while it still held its path, so the one thing a user does after retiring a
 * directory — register it again — came back as "already registered" naming a row
 * they could no longer see. Delete is the only exit, and because it takes the
 * boards and cards with it, it goes through `DeleteProjectDialog`.
 */

/**
 * The one way to remove a project, and therefore the place the cascade is spelled
 * out.
 *
 * The usage is fetched rather than derived from the boards already in memory,
 * because a card can override its board's project and the sidebar's list does not
 * know which — and because the same query is what the API will enforce against.
 * Until it arrives there is no button to press: consenting to "and everything
 * that uses it" without being told what that is is the failure this dialog
 * exists to prevent. The checkbox is required only when something is actually at
 * stake, so retiring an unused directory stays one click.
 */
export function DeleteProjectDialog({
  project,
  onClose,
  onDeleted,
}: {
  project: Project | null;
  onClose: () => void;
  onDeleted: (project: Project, result: { deletedBoards: number; deletedTasks: number }) => void;
}) {
  const [usage, setUsage] = useState<ProjectUsage | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUsage(null);
    setAcknowledged(false);
    setError(null);
    if (!project) return;
    let cancelled = false;
    api
      .projectUsage(project.id)
      .then((next) => !cancelled && setUsage(next))
      .catch((cause) => !cancelled && setError(cause instanceof ApiError ? cause.message : "Could not read what uses this project"));
    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  const detached = usage?.detachedBoards ?? [];
  const inUse =
    usage !== null && (usage.boards.length > 0 || usage.totalTasks > 0 || usage.agents.length > 0 || detached.length > 0);
  const confirm = async () => {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.deleteProject(project.id, { confirmCascade: inUse });
      onDeleted(project, result);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete that project");
    } finally {
      setBusy(false);
    }
  };

  return (
    // `[&>*]:min-w-0` because DialogContent is a grid, and a grid item's implicit
    // `min-width: auto` lets one unbreakable string — a board name, an imported
    // card's subject line, the path — widen the track past `max-w-lg` and give the
    // whole dialog a horizontal scrollbar. Every child below wraps or truncates,
    // so letting them shrink is the fix rather than a clip.
    <Dialog open={project !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <AlertTriangle className="size-4 shrink-0 text-destructive" />
            <span className="min-w-0 break-words">Delete {project?.name ?? "this project"}?</span>
          </DialogTitle>
          <DialogDescription>
            A project cannot be archived, so this is the only way to remove one — and it takes the work that pointed
            at it. Nothing inside{" "}
            <span className="break-all font-mono text-[11px] text-foreground">{project?.hostPath}</span> is touched: only
            this app's boards and cards go.
          </DialogDescription>
        </DialogHeader>

        {usage === null ? (
          <p className="text-xs text-muted-foreground">Checking what uses it…</p>
        ) : inUse ? (
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-destructive/30 bg-destructive/8 p-3 text-xs scrollbar-slim">
            {usage.boards.length > 0 ? (
              <div className="space-y-1">
                <p className="font-medium">
                  {usage.boards.length} board{usage.boards.length > 1 ? "s" : ""} will be deleted, with everything on
                  them:
                </p>
                <ul className="space-y-0.5">
                  {usage.boards.map((board) => (
                    <li key={board.id} className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate">{board.name}</span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {board.taskCount} card{board.taskCount === 1 ? "" : "s"}
                      </span>
                      {board.archived ? <Badge variant="outline">archived</Badge> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {usage.tasks.length > 0 ? (
              <div className="space-y-1">
                <p className="font-medium">
                  {usage.tasks.length} card{usage.tasks.length > 1 ? "s" : ""} on other boards name this project
                  themselves and will be deleted too:
                </p>
                <ul className="space-y-0.5">
                  {usage.tasks.slice(0, 6).map((task) => (
                    <li key={task.id} className="min-w-0 truncate">
                      {task.title} <span className="text-muted-foreground">· {task.boardName}</span>
                    </li>
                  ))}
                  {usage.tasks.length > 6 ? (
                    <li className="text-muted-foreground">and {usage.tasks.length - 6} more</li>
                  ) : null}
                </ul>
              </div>
            ) : null}

            {detached.length > 0 ? (
              <div className="space-y-1">
                <p className="font-medium">
                  {detached.length} board{detached.length > 1 ? "s" : ""} covering other projects too will only lose
                  this one, and stay:
                </p>
                <ul className="space-y-0.5">
                  {detached.map((board) => (
                    <li key={board.id} className="min-w-0 truncate">
                      {board.name}
                      {board.wasDefault ? (
                        <span className="text-muted-foreground">
                          {" "}
                          · its default
                          {board.inheritingTasks > 0
                            ? `; ${board.inheritingTasks} card${board.inheritingTasks === 1 ? "" : "s"} inheriting it will have no project`
                            : ""}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {usage.agents.length > 0 ? (
              <div className="space-y-1">
                <p className="font-medium">
                  {usage.agents.length} agent{usage.agents.length > 1 ? "s" : ""} working in this directory will be
                  deleted, with their conversations:
                </p>
                <ul className="space-y-0.5">
                  {usage.agents.map((agent) => (
                    <li key={agent.id} className="min-w-0 truncate">
                      {agent.name} <span className="text-muted-foreground">· @{agent.handle}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="text-muted-foreground">
              {usage.totalTasks} card{usage.totalTasks === 1 ? "" : "s"} in total. This cannot be undone. To keep the
              work and only change where it runs, close this and edit the project's directory instead.
            </p>
          </div>
        ) : (
          <p className="rounded-md border border-border bg-muted/50 p-2.5 text-xs">
            Nothing points at this project, so no board or card is affected.
          </p>
        )}

        {inUse ? (
          <label className="flex cursor-pointer items-start gap-2.5 text-xs leading-snug">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 size-3.5 shrink-0 accent-[var(--destructive)]"
            />
            <span className="min-w-0">
              I understand that the {usage.boards.length} board(s) and {usage.totalTasks} card(s) above are deleted
              along with this project{detached.length > 0 ? `, and that it is taken off ${detached.length} other board(s)` : ""}.
            </span>
          </label>
        ) : null}

        {error ? <p className="break-words text-xs text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirm()}
            loading={busy}
            disabled={usage === null || (inUse && !acknowledged)}
          >
            <Trash2 /> {inUse ? "Delete project and its work" : "Delete project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add and edit are one form: the fields, the validation and the failure modes are
 * identical, and the only difference is which request it sends.
 */
export function ProjectForm({
  project,
  onCancel,
  onSaved,
}: {
  project?: Project;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(project?.name ?? "");
  // The host form: it is the path the user knows, and the server maps it back.
  const [path, setPath] = useState(project?.hostPath ?? "");
  const [description, setDescription] = useState(project?.description ?? "");
  const [mergeCheck, setMergeCheck] = useState(project?.mergeCheck ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !path.trim()) {
      setError("A project needs a name and a directory");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), path: path.trim(), description: description.trim() || null, mergeCheck: mergeCheck.trim() || null };
      if (project) await api.updateProject(project.id, payload);
      else await api.createProject(payload);
      onSaved();
    } catch (cause) {
      // The path is checked on the server, because that is the machine the
      // directory has to exist on — so "no such directory" arrives as a 400 and
      // belongs here, next to the field the user has to fix.
      setError(cause instanceof ApiError ? cause.message : "Could not save that project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-ring/35 bg-surface/60 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nexus web"
            onKeyDown={(event) => event.key === "Enter" && void submit()}
          />
        </div>
        <div className="space-y-1.5">
          <Label hint="a path on your machine, or pick one below">Directory</Label>
          <Input
            value={path}
            onChange={(event) => setPath(event.target.value)}
            placeholder="~/Code/nexus-web"
            className="font-mono text-[12px]"
            onKeyDown={(event) => event.key === "Enter" && void submit()}
          />
        </div>
      </div>

      <FolderPicker
        selected={path}
        onPick={(entry) => {
          setPath(entry.hostPath);
          if (!name.trim()) setName(titleFromFolder(entry.name));
        }}
      />

      <div className="space-y-1.5">
        <Label hint="optional, but read by every delegated run">What is it?</Label>
        <Textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Next.js front end for the facilities portal. Run checks with `pnpm test`."
          // A long description gets room to be edited; a new one starts small.
          rows={(project?.description?.length ?? 0) > 240 ? 10 : 3}
        />
      </div>

      <div className="space-y-1.5">
        <Label hint="optional, one line of shell">Check before merging a parallel run</Label>
        <Input
          value={mergeCheck}
          onChange={(event) => setMergeCheck(event.target.value)}
          placeholder="bun run typecheck && bun test"
          className="font-mono text-[12px]"
          onKeyDown={(event) => event.key === "Enter" && void submit()}
        />
        <p className="text-[11px] leading-snug text-muted-foreground">
          Run in the run's worktree after it is rebased onto main. A failure keeps the branch out of main and hands the
          merge to the agent.
        </p>
      </div>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void submit()} loading={saving}>
          {project ? "Save" : "Register"}
        </Button>
      </div>
    </div>
  );
}

/** `nexus-web` → `Nexus web`: a starting point for the name, not a rule. */
const titleFromFolder = (folder: string): string => {
  const words = folder.replace(/[-_.]+/g, " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : folder;
};
